// Import runs as they go.
//
// The catalog import announces a run's progress, its end and any change to
// the assignments on `/rt/catalog/import` (aldoo-na/catalog-import
// lib/broadcast.js). A screen subscribes here and refetches when it hears
// one: the notice carries ids and counts, never the records.
//
//   useImportEvents({ onEvent })          everything — the admin
//   useImportEvents({ orgId, onEvent })   one vendor's own
import { useEffect, useRef } from "react"
import { useRealtime } from "aldoo-ra/Realtime/hooks"

export const IMPORT_EVENTS = ["import:progress", "import:finished", "import:assignments"]

const watchers = new Map()
const key = (socket, orgId) => `${socket.id || "socket"}:${orgId || "all"}`

const join = (socket, orgId) => {
  const id = key(socket, orgId)
  const count = watchers.get(id) || 0
  watchers.set(id, count + 1)
  if (count === 0) socket.emit("import:watch", { orgId })
}

const leave = (socket, orgId) => {
  const id = key(socket, orgId)
  const count = watchers.get(id) || 0
  if (count <= 1) {
    watchers.delete(id)
    socket.emit("import:unwatch", { orgId })
    return
  }
  watchers.set(id, count - 1)
}

export default function useImportEvents({ orgId = null, events = IMPORT_EVENTS, onEvent, enabled = true } = {}) {
  // the realtime loader turns the module folder's dashes into slashes, so the
  // channel is /rt/catalog/import
  const { socket, connected } = useRealtime(enabled ? "catalog/import" : null)
  const handler = useRef(onEvent)
  useEffect(() => {
    handler.current = onEvent
  }, [onEvent])

  useEffect(() => {
    if (!socket || !connected || !enabled) return
    join(socket, orgId)
    const listeners = events.map((event) => {
      const listener = (payload) => handler.current?.({ ...payload, event })
      socket.on(event, listener)
      return () => socket.off(event, listener)
    })
    return () => {
      leave(socket, orgId)
      listeners.forEach((off) => off())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, connected, enabled, orgId, events.join(",")])

  return { connected }
}
